'use client'

import { useState, useCallback, createContext, useContext, ReactNode } from 'react'

interface BookingLayoutProps {
  providerPanel: ReactNode
  bookingPanel: ReactNode
}

/**
 * Two-column layout for the booking page that collapses the provider panel
 * when the user advances past package selection (e.g. clicks "Select Package").
 * The collapsed state persists through the entire checkout flow.
 *
 * On mobile, columns stack vertically and the provider panel hides entirely
 * when collapsed. On desktop (lg+), the panel animates its width to zero.
 */
export function BookingLayout({ providerPanel, bookingPanel }: BookingLayoutProps) {
  const [collapsed, setCollapsed] = useState(false)

  const collapse = useCallback(() => setCollapsed(true), [])
  const expand = useCallback(() => setCollapsed(false), [])

  return (
    <div
      data-component-name="BookingLayout"
      className={`flex flex-col lg:flex-row transition-all duration-500 ease-in-out ${
        collapsed ? 'gap-0' : 'gap-6'
      }`}
    >
      {/* Provider Profile - Left Side */}
      <div
        className={`transition-all duration-500 ease-in-out overflow-hidden ${
          collapsed
            ? 'h-0 lg:h-auto lg:w-0 lg:min-w-0 opacity-0 pointer-events-none'
            : 'h-auto lg:w-1/2 lg:min-w-[50%] opacity-100'
        }`}
      >
        <div className="space-y-6">{providerPanel}</div>
      </div>

      {/* Booking Interface - Right Side */}
      <div className="transition-all duration-500 ease-in-out flex-1 min-w-0">
        <BookingLayoutContext.Provider value={{ collapsed, collapse, expand }}>
          {bookingPanel}
        </BookingLayoutContext.Provider>
      </div>
    </div>
  )
}

// Context so BookingInterface can trigger collapse/expand without prop drilling
interface BookingLayoutContextValue {
  collapsed: boolean
  collapse: () => void
  expand: () => void
}

const BookingLayoutContext = createContext<BookingLayoutContextValue>({
  collapsed: false,
  collapse: () => {},
  expand: () => {},
})

export function useBookingLayout() {
  return useContext(BookingLayoutContext)
}
